export function toError<T = object, TRes = T & Error>(
  e: unknown,
  defaultMessage = 'Unknown error',
): TRes {
  if (e instanceof Error) {
    return e as TRes;
  }

  if (typeof e === 'string') {
    return new Error(e) as TRes;
  }

  if (e) {
    try {
      return new Error(JSON.stringify(e)) as TRes;
    } catch (serializeError) {
      // Can throw on circular/non-serializable objects
      return new Error(String(serializeError)) as TRes;
    }
  }

  return new Error(defaultMessage) as TRes;
}

/**
 * An error's message for people to read. A callable that throws without a
 * message of its own rejects with just its code ("internal"), which says
 * nothing to them.
 */
export function errorMessage(e: unknown): string {
  const { message } = toError(e);
  const code = (e as { code?: unknown } | null)?.code;
  const bare =
    message === 'internal' ||
    (typeof code === 'string' && code.endsWith(`/${message}`));
  return bare
    ? 'Something went wrong on our side. Try again, or contact support if it keeps happening.'
    : message;
}

export interface BlockingFunctionError {
  message: string;
  reason?: string;
  status?: string;
}

const blockingFunctionErrorPattern =
  /HTTP Cloud Function returned an error: (\{.*\})/s;

/**
 * Firebase Auth reports an error thrown by one of our blocking functions
 * (e.g. beforeCreate) as `auth/internal-error`, with the function's JSON
 * response inlined in the message. Returns that response, or undefined when
 * the error didn't come from a blocking function.
 */
export function getBlockingFunctionError(
  e: unknown,
): BlockingFunctionError | undefined {
  const match = blockingFunctionErrorPattern.exec(toError(e).message);
  if (!match) return undefined;

  try {
    const { error } = JSON.parse(match[1]) as {
      error?: { message?: unknown; status?: unknown; details?: unknown };
    };
    if (typeof error?.message !== 'string' || !error.message) return undefined;

    const details = error.details as { reason?: unknown } | undefined;
    return {
      message: error.message,
      reason: typeof details?.reason === 'string' ? details.reason : undefined,
      status: typeof error.status === 'string' ? error.status : undefined,
    };
  } catch {
    return undefined;
  }
}

const genericAuthErrorMessage =
  'Something went wrong while signing in. Please try again.';

/**
 * The message to show for an error from a Firebase Auth call: what our
 * blocking function said, rather than Firebase's wrapper around it.
 */
export function getAuthErrorMessage(e: unknown): string {
  const blockingError = getBlockingFunctionError(e);
  if (blockingError) return blockingError.message;

  const { message } = toError(e);
  return blockingFunctionErrorPattern.test(message)
    ? genericAuthErrorMessage
    : message;
}
